"use client"

import { LocaleLink } from "../components/locale-link"
import { useAutoleadsLocale } from "../lib/locale-context"
import { useTr } from "../lib/use-tr"
import { ArrowRight } from "lucide-react"
import { useMemo, useState } from "react"

import type { BlogLocale, BlogPostMeta } from "./posts"
import { BLOG_LIST_T } from "./translations"

export type Category =
  | "all"
  | "guide"
  | "comparatif"
  | "rgpd"
  | "cas-client"
  | "produit"

export const CATEGORY_KEYS: Record<Category, keyof typeof BLOG_LIST_T> = {
  all: "all",
  guide: "guide",
  comparatif: "comparatif",
  rgpd: "rgpd",
  "cas-client": "casClient",
  produit: "produit",
}

const FILTERS: Category[] = [
  "all",
  "guide",
  "comparatif",
  "rgpd",
  "cas-client",
  "produit",
]

export function categorize(post: BlogPostMeta, locale: BlogLocale): Category {
  const slug = post.slug.toLowerCase()
  const audience = post.content[locale].audience.toLowerCase()
  if (slug.includes("vs-")) return "comparatif"
  if (slug.includes("rgpd") || slug.includes("gdpr")) return "rgpd"
  if (
    slug.includes("agenc") ||
    slug.includes("agency") ||
    audience.includes("agenc")
  ) {
    return "cas-client"
  }
  if (slug.includes("hubspot") || slug.includes("product")) return "produit"
  return "guide"
}

function PostCard({ post }: { post: BlogPostMeta }) {
  const locale = useAutoleadsLocale()
  const tr = useTr(BLOG_LIST_T)
  const content = post.content[locale]
  const dateLocale = locale === "FR" ? "fr-FR" : "en-GB"
  const category = categorize(post, locale)
  return (
    <LocaleLink
      href={`/blog/${post.slug}`}
      className="group bg-card ring-border/60 hover:ring-foreground/20 relative flex flex-col rounded-2xl p-6 ring-1 transition-all"
    >
      <div className="flex items-center gap-2 text-xs">
        <span className="bg-primary-cta/10 text-primary-cta rounded-full px-2.5 py-0.5 font-medium">
          {tr(CATEGORY_KEYS[category])}
        </span>
        <span className="text-muted-foreground">
          {post.readingMinutes} {tr("minRead")}
        </span>
      </div>
      <h3 className="font-display text-foreground group-hover:text-primary-cta mt-4 text-base font-semibold tracking-tight transition-colors">
        {content.title}
      </h3>
      <p className="text-muted-foreground mt-2 flex-1 text-sm leading-relaxed">
        {content.description}
      </p>
      <div className="border-border/60 mt-6 flex items-center justify-between border-t pt-4 text-xs">
        <span className="text-muted-foreground">
          {tr("by")} {post.author.name}
        </span>
        <time className="text-muted-foreground" dateTime={post.publishedAt}>
          {new Date(post.publishedAt).toLocaleDateString(dateLocale, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </time>
      </div>
      <span
        aria-hidden
        className="text-primary-cta absolute top-6 right-6 inline-flex size-7 items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100"
      >
        <ArrowRight className="size-4" />
      </span>
    </LocaleLink>
  )
}

export function BlogList({ posts }: { posts: BlogPostMeta[] }) {
  const locale = useAutoleadsLocale()
  const tr = useTr(BLOG_LIST_T)
  const [active, setActive] = useState<Category>("all")

  const filtered = useMemo(() => {
    if (active === "all") return posts
    return posts.filter((p) => categorize(p, locale) === active)
  }, [posts, active, locale])

  return (
    <section
      className="mx-auto mt-12 max-w-6xl"
      aria-labelledby="blog-list-heading"
    >
      <h2 id="blog-list-heading" className="sr-only">
        {tr("articles")}
      </h2>
      <div
        role="tablist"
        aria-label={tr("filterByCategory")}
        className="flex flex-wrap items-center justify-center gap-2"
      >
        {FILTERS.map((f) => {
          const isActive = active === f
          return (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(f)}
              className={
                isActive
                  ? "bg-foreground text-background text-2sm h-8 rounded-full px-3.5 font-medium transition-colors"
                  : "bg-card text-muted-foreground hover:text-foreground border-border/60 text-2sm h-8 rounded-full border px-3.5 font-medium transition-colors"
              }
            >
              {tr(CATEGORY_KEYS[f])}
            </button>
          )
        })}
      </div>

      {filtered.length > 0 ? (
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((post) => (
            <PostCard key={post.slug} post={post} />
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground mt-12 text-center text-sm">
          {tr("empty")}
        </p>
      )}
    </section>
  )
}
